import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useLocalization } from "../../../hooks/useLocalization";
import { fetchTeamOverview } from "../../../services/services";
import { getErrorMessage } from "../../../services/utils/apiError";
import type { ITeamOverview } from "../../../types/types";
import {
  formatSeasonLabel,
  isPrimaryPlayer,
  placeLabel,
  sortPlayersByPosition,
  teamDescription,
  type TeamTab,
} from "../constants";

export const useTeamPage = () => {
  const { teamId = "" } = useParams();
  const { strings, locale } = useLocalization();
  const copy = strings.TEAMS;
  const decodedTeamId = decodeURIComponent(teamId).trim();

  const [overview, setOverview] = useState<ITeamOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TeamTab>("roster");
  const [historySeason, setHistorySeason] = useState("all");
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!decodedTeamId) {
      setLoading(false);
      setError(copy.NOT_FOUND);
      return;
    }

    mountedRef.current = true;
    setLoading(true);
    setError(null);
    setOverview(null);
    setTab("roster");
    setHistorySeason("all");

    void (async () => {
      try {
        const data = await fetchTeamOverview(decodedTeamId);
        if (!mountedRef.current) return;
        if (!data || !data.team.id) {
          setError(copy.NOT_FOUND);
          setOverview(null);
          return;
        }
        setOverview(data);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(getErrorMessage(err, copy.LOAD_ERROR));
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, [decodedTeamId, copy.LOAD_ERROR, copy.NOT_FOUND]);

  const team = overview?.team;
  const description = team ? teamDescription(team, locale) : "";
  const primaryRoster = useMemo(
    () => sortPlayersByPosition((team?.players ?? []).filter((player) => isPrimaryPlayer(player))),
    [team?.players],
  );
  const substitutes = useMemo(
    () => sortPlayersByPosition((team?.players ?? []).filter((player) => !isPrimaryPlayer(player))),
    [team?.players],
  );

  const historySeasons = useMemo(() => {
    const seasons = new Set<string>();
    for (const match of overview?.matchHistory ?? []) {
      const season = match.season.trim();
      if (season) seasons.add(season);
    }
    return [...seasons];
  }, [overview?.matchHistory]);

  const filteredHistory = useMemo(() => {
    const entries = overview?.matchHistory ?? [];
    if (historySeason === "all") return entries;
    return entries.filter((match) => match.season === historySeason);
  }, [overview?.matchHistory, historySeason]);

  const championshipCount = useMemo(
    () => (overview?.placements ?? []).filter((row) => row.isChampion).length,
    [overview?.placements],
  );

  const tabs: { id: TeamTab; label: string }[] = [
    { id: "roster", label: copy.TAB_ROSTER },
    { id: "history", label: copy.TAB_HISTORY },
    { id: "placements", label: copy.TAB_PLACEMENTS },
  ];

  return {
    copy,
    strings,
    locale,
    decodedTeamId,
    overview,
    team,
    description,
    loading,
    error,
    tab,
    setTab,
    tabs,
    historySeason,
    setHistorySeason,
    historySeasons,
    filteredHistory,
    primaryRoster,
    substitutes,
    championshipCount,
    placements: overview?.placements ?? [],
    matchHistory: overview?.matchHistory ?? [],
    formatSeasonLabel,
    placeLabel: (place: number) => placeLabel(place, copy),
  };
};
